// @ts-check
import './typedefs';
import moment from 'moment-timezone';
import { camelCase } from 'lodash';

import { leavenURL } from './constants';

///////////////////////
// helpers
///////////////////////

const apiUrl = `${leavenURL}/api`;

class FetchError extends Error {
  constructor(...args) {
    super(...args);
    Error.captureStackTrace(this, FetchError);
  }
}

class FetchFail extends Error {
  constructor(...args) {
    super(...args);
    Error.captureStackTrace(this, FetchFail);
  }
}

function fetchErrorCheck(response) {
  return (results) => {
    /** handle http status codes not being 200 OK and JSend error formats */
    if (response.status !== 200 || results.status === 'error') {
      throw new FetchError(
        results.error ||
          results.detail ||
          results.message ||
          JSON.stringify(results)
      );
    }

    if (results.status === 'fail') {
      throw new FetchFail(
        (results.data && results.data.error) ||
          results.detail ||
          results.message ||
          JSON.stringify(results)
      );
    }

    return results;
  };
}

function urlWithQueryParams(url, query) {
  let urlFormatted;
  if (query) {
    const queryString = Object.keys(query)
      .filter((key) => query[key] != null)
      .map((key) => `${key}=${encodeURIComponent(query[key])}`)
      .join('&');
    urlFormatted = `${url}?${queryString}`;
  } else {
    urlFormatted = url;
  }

  return urlFormatted;
}

/**
 * Helper for fetching GET requests
 */
export async function get(url, query) {
  return fetch(urlWithQueryParams(url, query), {
    method: 'GET',
    credentials: 'include',
    mode: 'cors',
  }).then((response) => response.json().then(fetchErrorCheck(response)));
}

export function transformShifts(shifts) {
  // use sets to have unique filter properties
  const result = {
    shifts: [],
    topics: new Set(),
    languages: new Set(),
    partners: new Set(),
    locations: new Set(),
  };
  for (const shift of shifts) {
    const city = shift.job.city;
    const state = shift.job.state_province;
    const cityAndState = city && state ? `${city}, ${state}` : '';
    result.shifts.push({
      ...shift,
      job: {
        ...shift.job,
        city_and_state: cityAndState,
      },
      start_datetime: moment(shift.start_datetime),
    });
    for (const topic of shift.job.topics) {
      result.topics.add(topic);
    }
    // TODO loop through results if we allow multiple langs
    if (!shift.job.language) {
      // use English as default language
      shift.job.language = 'English';
    }
    result.languages.add(shift.job.language);
    const accountName = shift.job.campaign.account.name;
    if (accountName) {
      result.partners.add(accountName);
    }

    // add location if there are both city and state
    if (cityAndState) {
      result.locations.add(cityAndState);
    }
  }
  return result;
}

/**
 * Get upcoming conversations
 * @param {Object<string, any>} query
 * @returns {Promise<ConversationEvents>}
 */
export const getUpcomingConversations = (query) => {
  return get(`${apiUrl}/shifts`, query).then((results) =>
    transformShifts(results.data.shifts)
  );
};
